import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const useApi = (initialUrl, { method = 'GET', includeToken = false, successMessage = null, errorMessage = null } = {}) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const requestInProgress = useRef(false);

    const callApi = useCallback(async (payload = null, dynamicUrl = null) => {
        if (requestInProgress.current) {
            return;
        }

        const finalUrl = dynamicUrl || initialUrl;

        try {
            requestInProgress.current = true;
            setLoading(true);
            setError(null);

            let headers = {};

            if (includeToken) {
                const token = localStorage.getItem('token');
                if (token) {
                    const decodedToken = jwtDecode(token);
                    const userIdPermissao = decodedToken.usuario_id;

                    headers = {
                        Authorization: `Bearer ${token}`,
                        userIdPermissao: userIdPermissao
                    };
                }
            }

            const response = await axios({
                url: finalUrl,
                method,
                headers,
                ...(method === 'GET' ? {} : { data: payload }),
            });

            setData(response.data);

            if (successMessage) {
                message.success(successMessage);
            }

            return response.data;
        } catch (err) {
            setError(err);
            console.error('Erro na API:', err);

            if (err.response?.status === 401) {
                const errorMsg = err.response?.data?.error || err.response?.data?.detalhes || 'Autenticação falhou';
                message.error({
                    content: errorMsg,
                    key: 'auth-error',
                });

                navigate('/login');
                localStorage.setItem('redirectUrl', window.location.pathname);
            } else if (errorMessage) {
                message.error({
                    content: errorMessage,
                    key: 'api-error',
                });
            }

            throw err;
        } finally {
            setLoading(false);
            requestInProgress.current = false;
        }
    }, [initialUrl, method, includeToken, successMessage, errorMessage, navigate]);

    return { data, loading, error, callApi };
};

export default useApi;